import { Bot, ClipboardList, Edit, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import AgentFormPanel, {
  AgentFormValues,
} from '../components/agents/AgentFormPanel';
import TaskFormPanel, {
  TaskFormValues,
} from '../components/tasks/TaskFormPanel';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  Input,
  Modal,
  Pagination,
  ViewToggle,
} from '../components/ui';
import { useOrganization, usePage } from '../contexts';
import { useLocalStorage } from '../hooks';
import { AgentService, TaskService } from '../services';
import { Agent, Task } from '../types/entities';

type ToolMode = 'creative' | 'accuracy';
const CREATIVE_TOOL = 'ragtool';
const ACCURACY_TOOL = 'codeinterpretertool';
const ENABLED_CREW_TOOLS = [
  'ragtool',
  'codeinterpretertool',
  'filereadtool',
  'directoryreadtool',
  'directorysearchtool',
  'csvsearchtool',
  'jsonsearchtool',
  'txtsearchtool',
  'xmlsearchtool',
  'mdxsearchtool',
  'pdfsearchtool',
  'docxsearchtool',
  'websitesearchtool',
  'scrapewebsitetool',
  'scrapeelementfromwebsitetool',
  'youtubechannelsearchtool',
  'youtubevideosearchtool',
  'githubsearchtool',
] as const;
const DEFAULT_MAX_ITER = '5';
const DEFAULT_MAX_EXECUTION_TIME = '300';

const AgentsAndTasks: React.FC = () => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [showEditAgentModal, setShowEditAgentModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [showDeleteAgentModal, setShowDeleteAgentModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<number | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalAgents, setTotalAgents] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useLocalStorage<'full' | 'compact'>(
    'agentsAndTasks_viewMode',
    'full'
  );

  const { currentOrganization } = useOrganization();
  const { setPageInfo } = usePage();
  useEffect(() => {
    // Organization changed
    setCurrentPage(1);
  }, [currentOrganization]);

  // Set page title and description
  useEffect(() => {
    setPageInfo(t('agents.title'), t('agents.subtitle'));
  }, [setPageInfo, t]);
  const agentService = useMemo(() => new AgentService(), []);
  const taskService = useMemo(() => new TaskService(), []);

  const defaultAgentFormValues = useMemo<AgentFormValues>(
    () => ({
      name: '',
      role: '',
      goal: '',
      backstory: '',
      tools: [],
      tool_mode: 'accuracy',
      verbose: true,
      allow_delegation: true,
      max_iter: DEFAULT_MAX_ITER,
      max_execution_time: DEFAULT_MAX_EXECUTION_TIME,
      is_active: true,
    }),
    []
  );

  const defaultTaskFormValues = useMemo<TaskFormValues>(
    () => ({
      description: '',
      expected_output: '',
      tools: [],
      tool_mode: 'accuracy',
      context: [],
      is_active: true,
      result_format: '',
    }),
    []
  );

  const mapToolsToMode = useCallback((tools: string[] | undefined): ToolMode => {
    if (!Array.isArray(tools) || tools.length === 0) {
      return 'accuracy';
    }
    const normalizedTools = tools.map(tool => (tool || '').toLowerCase().trim());
    const hasRagTool = normalizedTools.includes(CREATIVE_TOOL);
    const hasCodeInterpreterTool = normalizedTools.includes(ACCURACY_TOOL);

    if (hasRagTool && !hasCodeInterpreterTool) {
      return 'creative';
    }
    if (hasCodeInterpreterTool && !hasRagTool) {
      return 'accuracy';
    }
    return 'accuracy';
  }, []);

  const mapModeToTools = useCallback(
    (mode: ToolMode): string[] =>
      mode === 'creative' ? [...ENABLED_CREW_TOOLS] : [...ENABLED_CREW_TOOLS],
    []
  );

  /**
   * Mapeia um agente para os valores do formulário.
   * @param agent Agente origem
   */
  const mapAgentToFormValues = (agent: Agent): AgentFormValues => ({
    name: agent.name,
    role: agent.role || '',
    goal: agent.goal || '',
    backstory: agent.backstory || '',
    tools: agent.tools || [],
    tool_mode: mapToolsToMode(agent.tools),
    verbose: agent.verbose,
    allow_delegation: agent.allow_delegation,
    max_iter: agent.max_iter?.toString() || '5',
    max_execution_time: agent.max_execution_time?.toString() || '300',
    is_active: agent.is_active,
  });

  /**
   * Mapeia uma task para os valores do formulário.
   * @param task Task origem
   */
  const mapTaskToFormValues = (task: Task): TaskFormValues => ({
    description: task.description || '',
    expected_output: task.expected_output || '',
    tools: task.tools || [],
    tool_mode: mapToolsToMode(task.tools),
    context: task.context || [],
    is_active: task.is_active,
    result_format: '',
  });

  const loadData = useCallback(async () => {
    if (!currentOrganization) {
      return;
    }

    try {
      setIsLoading(true);
      const [agentsData, tasksData] = await Promise.all([
        agentService.getAll({
          organization_id: currentOrganization.id,
          page: currentPage,
          page_size: pageSize,
        }),
        taskService.getAll({
          organization_id: currentOrganization.id,
          page: 1,
          page_size: 10,
        }),
      ]);

      // Remove duplicates by ID to prevent UI duplication
      const uniqueAgents = agentsData.items.filter(
        (agent, index, self) => index === self.findIndex(a => a.id === agent.id)
      );
      const uniqueTasks = tasksData.items.filter(
        (task, index, self) => index === self.findIndex(t => t.id === task.id)
      );

      setAgents(uniqueAgents);
      setTasks(uniqueTasks);
      setTotalAgents(agentsData.total);
      setTotalPages(Math.max(1, agentsData.total_pages || 1));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization, agentService, taskService, currentPage, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateAgent = async (values: AgentFormValues) => {
    if (!currentOrganization) {
      return;
    }

    try {
      const { tool_mode, ...agentValues } = values;
      const newAgent = await agentService.create({
        ...agentValues,
        organization_id: currentOrganization.id,
        tools: mapModeToTools(tool_mode),
        verbose: true,
        allow_delegation: true,
        max_iter: DEFAULT_MAX_ITER,
        max_execution_time: DEFAULT_MAX_EXECUTION_TIME,
      });

      setAgents(prev => [newAgent, ...prev]);
      setShowCreateAgentModal(false);
      toast.success(t('agents.agentCreatedSuccessfully'));
    } catch (error) {
      toast.error(t('agents.errorCreatingAgent'));
    }
  };

  const handleUpdateAgent = async (values: AgentFormValues) => {
    if (!editingAgent) {
      return;
    }

    try {
      const { tool_mode, ...agentValues } = values;
      const updatedAgent = await agentService.update(editingAgent.id, {
        ...agentValues,
        tools: mapModeToTools(tool_mode),
        verbose: true,
        allow_delegation: true,
        max_iter: DEFAULT_MAX_ITER,
        max_execution_time: DEFAULT_MAX_EXECUTION_TIME,
      });
      setAgents(prev =>
        prev.map(agent => (agent.id === editingAgent.id ? updatedAgent : agent))
      );
      setShowEditAgentModal(false);
      setEditingAgent(null);
      toast.success(t('agents.agentUpdatedSuccessfully'));
    } catch (error) {
      toast.error(t('agents.errorUpdatingAgent'));
    }
  };

  const handleDeleteAgentClick = (agentId: number) => {
    setAgentToDelete(agentId);
    setShowDeleteAgentModal(true);
  };

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;

    try {
      await agentService.delete(agentToDelete);
      setAgents(prev => prev.filter(agent => agent.id !== agentToDelete));
      setTasks(prev => prev.filter(task => task.agent_id !== agentToDelete));
      toast.success(t('agents.agentRemovedSuccessfully'));
      setShowDeleteAgentModal(false);
      setAgentToDelete(null);
    } catch (error) {
      toast.error(t('agents.errorRemovingAgent'));
    }
  };

  const handleCreateTask = async (values: TaskFormValues) => {
    if (!selectedAgent) {
      return;
    }

    try {
      const { tool_mode } = values;
      const newTask = await taskService.create({
        description: values.description,
        expected_output: values.expected_output,
        tools: mapModeToTools(tool_mode),
        context: values.context,
        is_active: values.is_active,
        agent_id: selectedAgent.id,
      });

      setTasks(prev => [newTask, ...prev]);
      setShowCreateTaskModal(false);
      setSelectedAgent(null);
      toast.success(t('tasks.taskCreatedSuccessfully'));
    } catch (error) {
      toast.error(t('tasks.errorCreatingTask'));
    }
  };

  const handleUpdateTask = async (values: TaskFormValues) => {
    if (!editingTask) {
      return;
    }

    try {
      const { tool_mode } = values;
      const updatedTask = await taskService.update(editingTask.id, {
        description: values.description,
        expected_output: values.expected_output,
        tools: mapModeToTools(tool_mode),
        context: values.context,
        is_active: values.is_active,
      });
      setTasks(prev =>
        prev.map(task => (task.id === editingTask.id ? updatedTask : task))
      );
      setShowEditTaskModal(false);
      setEditingTask(null);
      toast.success(t('tasks.taskUpdatedSuccessfully'));
    } catch (error) {
      toast.error(t('tasks.errorUpdatingTask'));
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await taskService.delete(taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));
      setShowEditTaskModal(false);
      setEditingTask(null);
      toast.success(t('tasks.taskRemovedSuccessfully'));
    } catch (error) {
      toast.error(t('tasks.errorRemovingTask'));
    }
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setShowEditAgentModal(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowEditTaskModal(true);
  };

  const filteredAgents = useMemo(() => {
    const filtered = agents.filter(
      agent =>
        (agent.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (agent.role?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );
    return filtered;
  }, [agents, searchQuery]);

  const getTasksForAgent = useCallback(
    (agentId: number) => {
      return tasks.filter(task => task.agent_id === agentId);
    },
    [tasks]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {t('agents.loadingAgentsAndTasks')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions and Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <Input
            placeholder={t('agents.searchAgents')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <ViewToggle
          viewMode={viewMode}
          onToggle={() =>
            setViewMode(prev => (prev === 'full' ? 'compact' : 'full'))
          }
        />
        <Button onClick={() => setShowCreateAgentModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('agents.newAgent')}
        </Button>
      </div>

      {/* Agents List */}
      <div
        className={
          viewMode === 'compact'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-6'
        }
      >
        {filteredAgents.map((agent, index) => {
          const agentTasks = getTasksForAgent(agent.id);

          return (
            <div
              key={`${agent.id}-${index}`}
              className={viewMode === 'compact' ? '' : 'space-y-4'}
            >
              {viewMode === 'compact' ? (
                /* Agent Card Compact */
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="p-1.5 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex-shrink-0">
                        <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                          {agent.name}
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
                          {agent.role || t('agents.noRoleDefined')}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge
                            variant={agent.is_active ? 'success' : 'secondary'}
                            size="sm"
                          >
                            {agent.is_active
                              ? t('agents.active')
                              : t('agents.inactive')}
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('tasks.title')}: {agentTasks.length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditAgent(agent)}
                        className="h-9 w-9 p-0"
                        title={t('common.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteAgentClick(agent.id)}
                        className="h-9 w-9 p-0"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {agentTasks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {agentTasks.map(task => (
                          <div
                            key={task.id}
                            className="flex-shrink-0 cursor-pointer"
                            onClick={() => handleEditTask(task)}
                            title={task.description}
                          >
                            <Badge
                              variant="secondary"
                              size="sm"
                              className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                            >
                              <ClipboardList className="h-3 w-3 mr-1" />
                              <span className="text-xs truncate max-w-[120px]">
                                {task.description}
                              </span>
                            </Badge>
                          </div>
                        ))}
                        <button
                          className="flex-shrink-0 border border-dashed border-gray-300 dark:border-gray-600 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          onClick={() => {
                            setSelectedAgent(agent);
                            setShowCreateTaskModal(true);
                          }}
                          title={t('tasks.createTask')}
                        >
                          <Plus className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}
                </Card>
              ) : (
                /* Agent Card Full */
                <Card className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                        <Bot className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {agent.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {agent.role || t('agents.noRoleDefined')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant={agent.is_active ? 'success' : 'secondary'}
                      >
                        {agent.is_active
                          ? t('agents.active')
                          : t('agents.inactive')}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditAgent(agent)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteAgentClick(agent.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('agents.goal')}:
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {agent.goal || t('agents.noGoalDefined')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('agents.backstory')}:
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {agent.backstory || t('agents.noBackstoryDefined')}
                      </p>
                    </div>
                  </div>

                  {/* Tasks Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {t('tasks.title')} ({agentTasks.length})
                      </h4>
                    </div>

                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {agentTasks.map(task => (
                        <div
                          key={task.id}
                          className="w-[190px] flex-shrink-0 cursor-pointer h-16"
                          onClick={() => handleEditTask(task)}
                        >
                          <Card className="hover:shadow-md transition-shadow h-full flex flex-col justify-center">
                            <div className="flex items-center space-x-2 mb-1">
                              <ClipboardList className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                              <span className="font-medium text-gray-900 dark:text-white text-xs">
                                {t('tasks.title')}
                              </span>
                            </div>

                            <div>
                              <p className="text-gray-600 dark:text-gray-400 text-xs truncate">
                                {task.description}
                              </p>
                            </div>
                          </Card>
                        </div>
                      ))}

                      {/* Add Task Button */}
                      <button
                        className="border-2 border-dashed border-light-300 dark:border-graphite-600 w-[190px] flex-shrink-0 rounded-lg h-16 flex items-center justify-center hover:bg-light-50 dark:hover:bg-graphite-800 transition-colors"
                        onClick={() => {
                          setSelectedAgent(agent);
                          setShowCreateTaskModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      </button>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          );
        })}
      </div>

      {totalAgents > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {totalAgents} {t('agents.title').toLowerCase()}
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {filteredAgents.length === 0 && (
        <div className="text-center py-12">
          <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('agents.noAgents')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery
              ? t('agents.tryAdjustSearch')
              : t('agents.createYourFirstAgent')}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowCreateAgentModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('agents.createFirstAgent')}
            </Button>
          )}
        </div>
      )}

      {/* Create Agent Modal */}
      <Modal
        isOpen={showCreateAgentModal}
        onClose={() => {
          setShowCreateAgentModal(false);
        }}
        title={t('agents.createAgent')}
        size="full"
      >
        <AgentFormPanel
          mode="create"
          organizationId={currentOrganization?.id}
          defaultValues={defaultAgentFormValues}
          submitLabel={t('agents.createAgent')}
          onSubmit={handleCreateAgent}
          onCancel={() => setShowCreateAgentModal(false)}
        />
      </Modal>

      {/* Edit Agent Modal */}
      <Modal
        isOpen={showEditAgentModal}
        onClose={() => {
          setShowEditAgentModal(false);
          setEditingAgent(null);
        }}
        title={t('agents.editAgent')}
        size="full"
      >
        {editingAgent && (
          <AgentFormPanel
            mode="edit"
            organizationId={currentOrganization?.id}
            defaultValues={mapAgentToFormValues(editingAgent)}
            submitLabel={t('agents.updateAgent')}
            onSubmit={handleUpdateAgent}
            onCancel={() => {
              setShowEditAgentModal(false);
              setEditingAgent(null);
            }}
          />
        )}
      </Modal>

      {/* Create Task Modal */}
      <Modal
        isOpen={showCreateTaskModal}
        onClose={() => {
          setShowCreateTaskModal(false);
          setSelectedAgent(null);
        }}
        title={`${t('tasks.newTaskFor')} ${selectedAgent?.name || ''}`}
        size="full"
      >
        <TaskFormPanel
          mode="create"
          organizationId={currentOrganization?.id}
          defaultValues={defaultTaskFormValues}
          submitLabel={t('tasks.createTaskButton')}
          onSubmit={handleCreateTask}
          onCancel={() => {
            setShowCreateTaskModal(false);
            setSelectedAgent(null);
          }}
          agentContext={selectedAgent}
        />
      </Modal>

      {/* Edit Task Modal */}
      <Modal
        isOpen={showEditTaskModal}
        onClose={() => {
          setShowEditTaskModal(false);
          setEditingTask(null);
        }}
        title={t('tasks.editTask')}
        size="full"
      >
        {editingTask && (
          <div className="flex flex-col h-full">
            <div className="flex justify-end p-4 border-b border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => setShowDeleteTaskModal(true)}
                className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('tasks.deleteTaskButton')}
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <TaskFormPanel
                mode="edit"
                organizationId={currentOrganization?.id}
                defaultValues={mapTaskToFormValues(editingTask)}
                submitLabel={t('tasks.updateTask')}
                onSubmit={handleUpdateTask}
                onCancel={() => {
                  setShowEditTaskModal(false);
                  setEditingTask(null);
                }}
                agentContext={
                  editingTask.agent_id
                    ? agents.find(agent => agent.id === editingTask.agent_id) ||
                      null
                    : null
                }
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Task Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteTaskModal}
        onClose={() => {
          setShowDeleteTaskModal(false);
        }}
        onConfirm={() => {
          if (editingTask) {
            handleDeleteTask(editingTask.id);
            setShowDeleteTaskModal(false);
            setShowEditTaskModal(false);
            setEditingTask(null);
          }
        }}
        message={t('tasks.confirmDeleteTask')}
        variant="danger"
      />

      {/* Delete Agent Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteAgentModal}
        onClose={() => {
          setShowDeleteAgentModal(false);
          setAgentToDelete(null);
        }}
        onConfirm={handleDeleteAgent}
        message={t('agents.confirmDeleteAgent')}
        variant="danger"
      />
    </div>
  );
};

export default AgentsAndTasks;
